'use client';

import { ReactNode } from 'react';
import styled from '@emotion/styled';
import {
	ArrowIconLight,
	Css,
	IResponsiveImageSource,
	IStyledCardProps,
	Responsive,
	StyledCardContent,
	StyledCardLink,
	StyledCardPicture,
} from '@/ui';

import { css } from '@emotion/core';
import { ArrowLinkStyles } from '../../button/ArrowLinkStyles';
import BaseCard from '../BaseCard';
import { renderMarkdownAsPlainText } from '../../../../utils/renderMarkdownAsPlainText';
import { Color } from '../../../../styles/Color';
import { theme } from '../../../../styles/theme';
import { formatFullDate } from '../../../../i18n/formatFullDate';
import { getHeadingStyle } from '../../../../styles/utils/getHeadingStyle';
import { IContentCategoryEntry } from '../../../../contentful/types/IContentCategoryEntry';

interface ICardProps extends IStyledCardProps {
	title: string | ReactNode;
	body?: string | ReactNode;
	imageSource?: string | IResponsiveImageSource;
	imageAlt?: string;
	linkHref?: string;
	linkTitle?: string;
	fullyClickable?: boolean;
	publicationDate?: string;
	isBackgroundSolid?: boolean;
	categories?: IContentCategoryEntry[];
	flowy?: boolean;
}

export default function Card({
	title,
	body,
	linkHref,
	linkTitle,
	imageSource,
	fullyClickable = true,
	publicationDate,
	imageAlt,
	isBackgroundSolid = false,
	categories,
	flowy,
	...props
}: ICardProps) {
	const filteredBody = renderMarkdownAsPlainText(body);

	return (
		<BaseCard fullyClickable={fullyClickable} linkHref={linkHref} {...props}>
			{imageSource && (
				<CardPictureStyled
					source={imageSource}
					alt={imageAlt}
					fullyClickable={fullyClickable}
				/>
			)}
			<CardContentStyled
				fullyClickable={fullyClickable}
				isBackgroundSolid={isBackgroundSolid}
				flowy={flowy}
			>
				{categories?.length && (
					<CardCategories>
						{categories.map((category) => (
							<CardCategory key={category.id}>
								{category.fields.categoryLabel}
							</CardCategory>
						))}
					</CardCategories>
				)}

				<div>
					<PublicationDate>
						{publicationDate && formatFullDate(publicationDate, 'MMM d, yyyy')}
					</PublicationDate>
					{typeof title === 'string' ? (
						<CardTitle aria-label={title}>{title}</CardTitle>
					) : (
						title
					)}
					{filteredBody && <CardBody>{filteredBody}</CardBody>}
				</div>
				{fullyClickable && (
					<CardLinkText>
						<span>{linkTitle ?? 'Read more'}</span> <ArrowIconLight />
					</CardLinkText>
				)}
			</CardContentStyled>
		</BaseCard>
	);
}

export const CardPictureStyled = styled(StyledCardPicture)<{
	fullyClickable: boolean;
}>`
	img {
		border-radius: ${({ fullyClickable }) =>
			fullyClickable ? `16px 16px 0 0` : `16px 16px 16px 16px`};
		aspect-ratio: 2/1;
		width: 100%;
		object-fit: cover;

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			height: ${Css.pxToRem(140)};
		}
	}
`;

export const CardContentStyled = styled(StyledCardContent)<{
	fullyClickable: boolean;
	isBackgroundSolid: boolean;
	flowy?: boolean;
}>`
	flex: 1;
	padding: ${Css.pxToRem(24)} ${Css.pxToRem(16)} ${Css.pxToRem(16)};

	${({ isBackgroundSolid }) =>
		isBackgroundSolid &&
		css`
			border-radius: 16px 16px 16px 16px;
			background-color: ${Color.PrimaryWhite};
		`}

	${({ fullyClickable }) =>
		!fullyClickable &&
		css`
			padding-top: ${Css.pxToRem(14)};
		`}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		padding: ${Css.pxToRem(18)} ${Css.pxToRem(8)} ${Css.pxToRem(18)};

		${({ flowy }) =>
			flowy &&
			css`
				padding: ${Css.pxToRem(16)};
			`}

		${({ fullyClickable, flowy }) =>
			!fullyClickable &&
			!flowy &&
			css`
				padding: ${Css.pxToRem(14)} 0 ${Css.pxToRem(14)};
			`}
	}
`;

export const PublicationDate = styled.div`
	margin-bottom: ${Css.pxToRem(4)};
	color: ${Color.OnBackgroundText};
`;

export const CardTitle = styled.h3`
	${getHeadingStyle(theme, 'h6')}
	overflow: hidden;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
`;

export const CardBody = styled.div`
	font-size: ${Css.pxToRemFromTheme(18)};
	margin-top: ${Css.pxToRemFromTheme(8)};
	color: ${Color.OnBackgroundText};
	overflow: hidden;
	display: -webkit-box;
	-webkit-line-clamp: 3;
	-webkit-box-orient: vertical;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: ${Css.pxToRemFromTheme(16)};
		-webkit-line-clamp: 2;
	}
`;

export const CardLinkStyled = styled(StyledCardLink)`
	${ArrowLinkStyles}
	margin-top: ${Css.pxToRemFromTheme(28)};
	display: flex;
	align-self: flex-end;
`;

export const CardLinkText = styled.div`
	${ArrowLinkStyles}
	margin-top: ${Css.pxToRemFromTheme(28)};
	display: flex;
	align-self: flex-end;
	align-items: center;
	gap: ${theme.button?.iconSeparator};

	svg {
		path {
			fill: ${Color.PrimaryBlue};
		}
	}
`;

const CardCategories = styled.div`
	position: absolute;
	display: flex;
	flex-wrap: wrap;
	gap: ${Css.pxToRem(4)};
	top: ${Css.pxToRem(8)};
	left: ${Css.pxToRem(8)};
`;

const CardCategory = styled.div`
	background-color: ${Color.PrimaryWhite};
	padding: ${Css.pxToRem(8)} ${Css.pxToRem(16)};
	border-radius: ${Css.pxToRem(12)};
	color: ${Color.OnBackgroundText};
`;
